<template>
  <div class="custom-validations-wrapper">
    <!-- Reachable only by typing the URL: the tab link is hidden for other company types. -->
    <div v-if="!isUnderwriter" class="rules-card">
      <p class="text-muted mb-0">
        Custom Validations are only available for underwriter companies, and {{ company.name }}
        is not one.
      </p>
    </div>

    <div v-else-if="loading" class="py-4 text-center">
      <cv-spinner class="mr-2" />
      Loading custom validations...
    </div>

    <b-alert v-else-if="loadError" show variant="danger">
      {{ loadError }}
    </b-alert>

    <template v-else>
      <div class="d-flex align-items-center mb-2">
        <label class="mb-0 mr-5 font-weight-bold">
          Enable Custom Validations?
        </label>

        <base-switch
          id="custom-validations-switch"
          :value="enabled"
          :disabled="!canToggle"
          :class="{ 'switch-disabled': !canToggle }"
          onText=""
          offText=""
          @click.prevent="() => onToggleClick()"
        ></base-switch>
      </div>

      <p v-if="!canManage" class="text-muted small mb-4">
        Only LimeIQ superusers can change this setting.
      </p>
      <p v-else-if="!subscriptionActive && !enabled" class="text-muted small mb-4">
        {{ company.name }} doesn't have an active Custom Validations subscription. Set one up in
        the company's billing configuration before switching this on.
      </p>

      <!-- On: the rules table (CV-4, LIQ-2271). -->
      <custom-validation-rules-table
        v-if="enabled"
        class="mt-4"
        :company="company"
        :rules="rules"
        :jurisdictions="jurisdictions"
        :can-manage="canManage"
        @changed="refreshRules"
      />

      <!-- Off: empty state -->
      <div v-else class="rules-card mt-4">
        <p class="font-weight-bold mb-1">Custom Validations are off</p>
        <p class="text-muted mb-0">
          {{ company.name }} validates its BDX on the standard LimeIQ rules. Switch Custom
          Validations on to manage its rules.
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import CustomValidationsService, {
  canManageCustomValidations
} from '@services/customValidationsService'
import CustomValidationRulesTable from './CustomValidationRulesTable.vue'
import CvSpinner from './CvSpinner.vue'

const errorMessage = (err, fallback) =>
  (err && err.response && err.response.data && err.response.data.detail) || fallback

export default {
  name: 'ManageCompanyCustomValidations',

  components: { CustomValidationRulesTable, CvSpinner },

  props: {
    company: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      loading: true,
      loadError: null,
      enabled: false,
      subscriptionActive: false,
      rules: [],
      jurisdictions: [],
      saving: false
    }
  },

  computed: {
    isUnderwriter() {
      return !!(this.company.role && this.company.role.role === 'Underwriter')
    },
    canManage() {
      return canManageCustomValidations(this.$store.getters.user)
    },
    // Switching off never needs a subscription; switching on does.
    canToggle() {
      return this.canManage && !this.saving && (this.enabled || this.subscriptionActive)
    }
  },

  watch: {
    company: {
      immediate: true,
      handler(val) {
        if (!val || !val.id) return
        // No API call for a non-underwriter company - the template explains instead.
        if (this.isUnderwriter) this.load()
        else this.loading = false
      }
    }
  },

  methods: {
    async load() {
      this.loading = true
      this.loadError = null
      try {
        this.applyLoaded(await CustomValidationsService.getCustomValidations(this.company.id))
      } catch (err) {
        this.loadError = errorMessage(err, 'Failed to load custom validations.')
      } finally {
        this.loading = false
      }
    },

    // After a rule changes: reload without the page-wide spinner, so the table stays in place.
    async refreshRules() {
      try {
        this.applyLoaded(await CustomValidationsService.getCustomValidations(this.company.id))
      } catch (err) {
        this.$bvToast.toast(errorMessage(err, 'Failed to reload the rules.'), {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      }
    },

    applyLoaded({ data }) {
      this.enabled = !!data.enabled
      this.subscriptionActive = !!data.subscription_active
      this.rules = data.rules || []
      this.jurisdictions = data.jurisdictions || []
    },

    onToggleClick() {
      if (!this.canToggle) return

      if (this.enabled) {
        this.confirmSwitchOff()
      } else {
        this.saveEnabled(true)
      }
    },

    async confirmSwitchOff() {
      await this.$swal({
        title: 'Switch off Custom Validations?',
        text:
          'Custom rules stop applying from the next validation run. The configuration is kept, ' +
          'so switching back on restores it. LimeIQ default rules are not affected - they always run.',
        showCancelButton: true,
        confirmButtonText: 'SWITCH OFF',
        cancelButtonText: 'CANCEL',
        showLoaderOnConfirm: true,
        allowOutsideClick: false,
        preConfirm: async (confirm) => {
          if (!confirm) return false
          const saved = await this.saveEnabled(false, { quiet: true })
          if (!saved.ok) {
            this.$swal.showValidationMessage(saved.message)
            return false
          }
          this.toastSaved(false)
        }
      })
    },

    // Returns { ok, message } so the switch-off dialog can show a failure inside itself.
    async saveEnabled(enabled, { quiet = false } = {}) {
      this.saving = true
      try {
        const { data } = await CustomValidationsService.setEnabled(this.company.id, enabled)
        this.enabled = !!data.enabled
        this.subscriptionActive = !!data.subscription_active
        if (!quiet) this.toastSaved(this.enabled)
        return { ok: true }
      } catch (err) {
        const message = errorMessage(err, 'Failed to save Custom Validations setting.')
        if (!quiet) {
          this.$bvToast.toast(message, { title: 'Error', variant: 'danger', solid: true })
        }
        return { ok: false, message }
      } finally {
        this.saving = false
      }
    },

    toastSaved(enabled) {
      this.$bvToast.toast(
        `Custom Validations switched ${enabled ? 'on' : 'off'} for ${this.company.name}.`,
        { title: 'Success', variant: 'success', solid: true }
      )
    }
  }
}
</script>

<style>
.custom-validations-wrapper {
  padding-top: 8px;
}

.custom-validations-wrapper .switch-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.custom-validations-wrapper .rules-card {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px 24px;
  max-width: 580px;
}
</style>
